'use client';

import { useEffect, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { cn } from '../../../utils/cn';
import { NotificationTile } from './notification-tile';
import { useOptionalNotifications } from './notifications-context';
import type { RenderNotificationTile, Notification } from './types';

export type NotificationPopupsPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export interface NotificationPopupsProps {
  className?: string;
  liveDurationMs?: number;
  maxVisible?: number;
  position?: NotificationPopupsPosition;
  hideWhenDrawerOpen?: boolean;
  renderTile?: RenderNotificationTile;
}

const positionClasses: Record<NotificationPopupsPosition, string> = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4 flex-col-reverse',
  'bottom-left': 'bottom-4 left-4 flex-col-reverse',
};

export function NotificationPopups({
  className,
  liveDurationMs = 4000,
  maxVisible = 4,
  position = 'top-right',
  hideWhenDrawerOpen = true,
  renderTile: renderTileProp,
}: NotificationPopupsProps) {
  const ctx = useOptionalNotifications();

  // The age filter below needs the CURRENT time, and reading a clock in the
  // render body makes the output depend on something React cannot replay. So
  // `now` is state, advanced by an interval that only runs while something
  // could still age out — an idle popup container ticks nothing.
  //
  // Both hooks sit above every early return on purpose: `ctx` may be absent
  // and `showPopups` may flip at any time, and a hook below those branches
  // would change how many hooks this component renders.
  const [now, setNow] = useState(() => Date.now());
  const hasAgeingNotifications = Boolean(ctx?.notifications.some(n => !n.read && !n.settled));
  useEffect(() => {
    if (!hasAgeingNotifications) return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [hasAgeingNotifications]);

  if (!ctx) return null;

  const { notifications, showPopups, isOpen, open, markRead, markSettled, renderTile: ctxRenderTile } = ctx;
  const renderTile = renderTileProp ?? ctxRenderTile;

  if (!showPopups) return null;
  if (hideWhenDrawerOpen && isOpen) return null;

  // Drops anything created while the popups were hidden — a notification that
  // arrived behind an open drawer must not pop up late when the drawer closes.
  const live = notifications
    .filter(n => !n.read && !n.settled && now - n.createdAt < liveDurationMs)
    .slice(0, maxVisible);

  if (live.length === 0) return null;

  const activate = (notification: Notification) => {
    if (notification.onClick) {
      notification.onClick();
      markRead(notification.id);
      markSettled(notification.id);
      return;
    }
    open();
    markSettled(notification.id);
  };

  const handleBodyClick = (notification: Notification) => (event: MouseEvent<HTMLDivElement>) => {
    // Nested interactive controls (X, check button) handle their own actions.
    if ((event.target as HTMLElement).closest('button')) return;
    activate(notification);
  };

  const handleBodyKeyDown = (notification: Notification) => (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button')) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    activate(notification);
  };

  return (
    <div
      aria-live="polite"
      className={cn(
        'pointer-events-none fixed z-[9999] flex w-[26rem] max-w-[calc(100vw-2rem)] flex-col gap-[var(--spacing-system-xs)]',
        positionClasses[position],
        className,
      )}
    >
      {live.map(n => {
        const custom = renderTile?.(n, { onComplete: markRead, onSettle: markSettled, liveDurationMs });
        if (custom) {
          return (
            <div key={n.id} className="pointer-events-auto">
              {custom}
            </div>
          );
        }
        return (
          // `role="button"` rather than a real <button>: the row hosts its own
          // nested interactive elements, which a <button> may not contain.
          <div
            key={n.id}
            role="button"
            tabIndex={0}
            onClick={handleBodyClick(n)}
            onKeyDown={handleBodyKeyDown(n)}
            className="pointer-events-auto cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ods-accent"
          >
            <NotificationTile
              notification={n}
              liveDurationMs={liveDurationMs}
              onComplete={markRead}
              onSettle={markSettled}
            />
          </div>
        );
      })}
    </div>
  );
}
